import { useId } from 'react';

/**
 * Inline brand mark (no <img>) so reloads never show a broken image, SVG filters render
 * reliably in-document, and multiple headers can coexist with unique gradient/filter ids.
 */
export function LogoMarkSvg({ variant, className = '', style }) {
  const raw = useId().replace(/:/g, '');
  const p = (s) => `${raw}_${s}`;

  if (variant === 'dark') {
    const pinGrad = p('pinGrad');
    const pinShine = p('pinShine');
    const ringGrad = p('ringGrad');
    const ringGlow = p('ringGlow');
    const dotGlow = p('dotGlow');
    const baseGlow = p('baseGlow');

    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 120 140"
        fill="none"
        className={className}
        style={style}
        aria-hidden
        focusable="false"
      >
        <defs>
          <linearGradient id={pinGrad} x1="0" y1="0" x2="1" y2="1" gradientUnits="objectBoundingBox">
            <stop offset="0%" stopColor="#38BDF8" />
            <stop offset="35%" stopColor="#6366F1" />
            <stop offset="70%" stopColor="#A855F7" />
            <stop offset="100%" stopColor="#C084FC" />
          </linearGradient>
          <linearGradient id={pinShine} x1="0.2" y1="0" x2="0.8" y2="1" gradientUnits="objectBoundingBox">
            <stop offset="0%" stopColor="#fff" stopOpacity="0.3" />
            <stop offset="50%" stopColor="#fff" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#fff" stopOpacity="0" />
          </linearGradient>
          <linearGradient id={ringGrad} x1="0" y1="0" x2="1" y2="1" gradientUnits="objectBoundingBox">
            <stop offset="0%" stopColor="#38BDF8" stopOpacity="0.9" />
            <stop offset="50%" stopColor="#818CF8" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#C084FC" stopOpacity="0.3" />
          </linearGradient>
          <filter id={ringGlow} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id={dotGlow} x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <radialGradient id={baseGlow} cx="0.5" cy="1" r="0.5" gradientUnits="objectBoundingBox">
            <stop offset="0%" stopColor="#38BDF8" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#38BDF8" stopOpacity="0" />
          </radialGradient>
        </defs>
        <ellipse cx="60" cy="118" rx="32" ry="6" fill={`url(#${baseGlow})`} />
        <circle
          cx="60"
          cy="58"
          r="52"
          stroke={`url(#${ringGrad})`}
          strokeWidth="2.5"
          fill="none"
          filter={`url(#${ringGlow})`}
        />
        <circle cx="97" cy="20" r="6" fill="#38BDF8" filter={`url(#${dotGlow})`} />
        <path
          d="M60 12C41.222 12 26 27.222 26 46c0 7.5 3.2 15 8.5 22.5C42 79 60 102 60 102s18-23 25.5-33.5C90.8 61 94 53.5 94 46c0-18.778-15.222-34-34-34z"
          fill={`url(#${pinGrad})`}
        />
        <path
          d="M60 12C41.222 12 26 27.222 26 46c0 7.5 3.2 15 8.5 22.5C42 79 52 88 56 94c0 0-18-30-18-48 0-16 10-30 28-32 4-.4 6 .2 6 .2S78.778 12 60 12z"
          fill={`url(#${pinShine})`}
        />
        <path d="M50 33L76 48L50 63z" fill="#fff" fillOpacity="0.95" />
      </svg>
    );
  }

  const pinGrad = p('pinGrad');
  const pinShine = p('pinShine');
  const ringGrad = p('ringGrad');

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 120 140"
      fill="none"
      className={className}
      style={style}
      aria-hidden
      focusable="false"
    >
      <defs>
        <linearGradient id={pinGrad} x1="0" y1="0" x2="1" y2="1" gradientUnits="objectBoundingBox">
          <stop offset="0%" stopColor="#22D3EE" />
          <stop offset="35%" stopColor="#3B82F6" />
          <stop offset="70%" stopColor="#7C3AED" />
          <stop offset="100%" stopColor="#A855F7" />
        </linearGradient>
        <linearGradient id={pinShine} x1="0.2" y1="0" x2="0.8" y2="1" gradientUnits="objectBoundingBox">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.35" />
          <stop offset="50%" stopColor="#fff" stopOpacity="0.08" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={ringGrad} x1="0" y1="0" x2="1" y2="1" gradientUnits="objectBoundingBox">
          <stop offset="0%" stopColor="#22D3EE" stopOpacity="0.6" />
          <stop offset="50%" stopColor="#A78BFA" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#C084FC" stopOpacity="0.15" />
        </linearGradient>
      </defs>
      <circle cx="60" cy="58" r="52" stroke={`url(#${ringGrad})`} strokeWidth="3" fill="none" />
      <circle cx="97" cy="20" r="6" fill="#22D3EE" />
      <path
        d="M60 12C41.222 12 26 27.222 26 46c0 7.5 3.2 15 8.5 22.5C42 79 60 102 60 102s18-23 25.5-33.5C90.8 61 94 53.5 94 46c0-18.778-15.222-34-34-34z"
        fill={`url(#${pinGrad})`}
      />
      <path
        d="M60 12C41.222 12 26 27.222 26 46c0 7.5 3.2 15 8.5 22.5C42 79 52 88 56 94c0 0-18-30-18-48 0-16 10-30 28-32 4-.4 6 .2 6 .2S78.778 12 60 12z"
        fill={`url(#${pinShine})`}
      />
      <path d="M50 33L76 48L50 63z" fill="#fff" fillOpacity="0.95" />
    </svg>
  );
}
